import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'

import * as Sentry from '@sentry/react'

import { getAppointmentTimeslot } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { Programs } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programConfigs'
import { isInitialEvaluationSessionType } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Note, NoteStatusStates, NoteTypes } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'
import { TransferStatus } from '@lyrahealth-inc/shared-app-logic/src/models/transfer/Transfer'

import { CLIENT_END_OF_SESSION, CLIENT_NEW_NOTE, CLIENT_NOTE } from '../../../common/constants/routingConstants'
import { isEngageDAProvider, resolveDAEngageProgramName } from '../../../common/utils/utils'
import { getAuthRoles, getAuthUserId } from '../../../data/auth/authSelectors'
import { useAppDispatch } from '../../../data/store'
import { populateInitialValues } from '../../../lyraTherapy/clientNotes/ClientNotes'
import { useGetNotesQuery, useLazyGetNoteQuery } from '../../../lyraTherapy/clientNotes/data/notesApi'
import { fetchAppointmentChargeGuard } from '../../../lyraTherapy/clientNotes/utils/daFlexibleNoteChargeGuard'
import { DA_FLEXIBLE_NOTE_NAME } from '../../../lyraTherapy/clientNotes/utils/noteConstants'
import { setAppointment } from '../../../lyraTherapy/clients/clientDetails/data/appointmentsSlice'
import { setSelectedClientId } from '../../../lyraTherapy/clients/clientDetails/data/ltClientSlice'
import { useLazyGetContentsByProgramIdQuery } from '../../../lyraTherapy/data/contentsMetadataApi'
import { setToastContent } from '../../../lyraTherapy/data/ltToastAutoActions'
import { useLazyGetClientEpisodesQuery } from '../../../lyraTherapy/episodes/data/episodesApi'
import { useLazyGetTransfersQuery } from '../../../payments/data/transfersApi'
import { OPEN_ITEMS_LOOKBACK_MONTHS } from '../constants'

type UseNavigateToCloseSessionParams = {
  months?: number
  originRoute?: string
  skipNotes?: boolean
}

export const useNavigateToCloseSession = ({
  months = OPEN_ITEMS_LOOKBACK_MONTHS,
  originRoute = 'tasks',
  skipNotes = false,
}: UseNavigateToCloseSessionParams = {}) => {
  const userId = useSelector(getAuthUserId)
  const userRoles = useSelector(getAuthRoles)
  const { enableDAFlexibleNote } = useFlags()
  const intl = useIntl()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const {
    data: allNotes,
    isSuccess: notesFetched,
    isLoading: isLoadingNotes,
  } = useGetNotesQuery(
    {
      providerId: userId,
      months,
      note_types: [NoteTypes.INITIAL, NoteTypes.SESSION],
      excludedFields: ['body', 'content_meta_data'],
    },
    { skip: skipNotes },
  )

  const [fetchTransfers] = useLazyGetTransfersQuery()
  const [fetchNote] = useLazyGetNoteQuery()
  const [getClientEpisodes] = useLazyGetClientEpisodesQuery()
  const [getContentsByProgramId] = useLazyGetContentsByProgramIdQuery()

  const goToExistingNote = useCallback(
    async (
      appointment: Appointment,
      note: { id: string },
      appointmentCharge?: { id: string },
      prefetchedNote?: Note,
    ) => {
      const fullNote = prefetchedNote ?? (await fetchNote(note.id).unwrap())
      navigate(`${CLIENT_NOTE.route}?id=${note.id}`, {
        state: {
          note: { ...fullNote },
          chargeId: appointmentCharge?.id,
          needToUpdateCharge: appointment.changeReason !== 'clientIneligible',
          originRoute,
        },
      })
    },
    [fetchNote, navigate, originRoute],
  )

  // Shared by both callbacks: without a charge lookup we can't tell the flexible-note flow from the legacy
  // one, so surface the failure rather than guessing a flow.
  const dispatchChargeGuardError = useCallback(() => {
    dispatch(
      setToastContent({
        text: intl.formatMessage({
          defaultMessage: 'Unable to load note. Please try again.',
          description: 'Error shown when the charge lookup that picks the session note flow fails',
        }),
        id: 'useNavigateToCloseSession-charge-guard-error',
        toastType: 'error',
      }),
    )
  }, [dispatch, intl])

  const closeSession = useCallback(
    async (appointment: Appointment, clientId: string) => {
      dispatch(setSelectedClientId(clientId))
      dispatch(setAppointment(appointment))

      let legacyFullNote: Note | undefined

      if (enableDAFlexibleNote && isEngageDAProvider(userRoles)) {
        const existingNote = allNotes?.find((n) => n.appointment_id === appointment.appointmentId)

        // When a note exists it alone decides the flow: it records which flow closed the session. The
        // charge is only consulted when there is no note, and then any charge means the legacy flow, since
        // the flexible note refuses to create a charge without a saved note.
        if (existingNote) {
          const fullNote = await fetchNote(existingNote.id).unwrap()
          if (fullNote.content_meta_data?.name === DA_FLEXIBLE_NOTE_NAME) {
            await goToExistingNote(appointment, existingNote, undefined, fullNote)
            return
          }
          legacyFullNote = fullNote
        }

        const chargeGuard = !existingNote
          ? await fetchAppointmentChargeGuard(fetchTransfers, {
              providerId: userId,
              clientId,
              appointmentId: appointment.appointmentId,
            })
          : undefined

        if (chargeGuard?.failed) {
          dispatchChargeGuardError()
          return
        }

        if (!existingNote && !chargeGuard?.hasCharge && appointment.programId) {
          const [contents, episodes] = await Promise.all([
            getContentsByProgramId({ programId: appointment.programId }).unwrap(),
            getClientEpisodes({ providerId: userId, patientId: clientId }).unwrap(),
          ])

          const appointmentEpisode = episodes?.find((e) => e.id === appointment.episodeId)
          if (contents && appointmentEpisode) {
            const flexibleNoteMetadata = contents.find((c) => c.name === DA_FLEXIBLE_NOTE_NAME)
            if (flexibleNoteMetadata) {
              const body = populateInitialValues([], DA_FLEXIBLE_NOTE_NAME, contents)
              const appointmentTimeslot = getAppointmentTimeslot({ appointment, intl })
              // Prefer suggestedSessionType (set on retro appointments) over chronological sessionNumber
              // so a retro follow-up — e.g. a backdated appointment created after the initial was already
              // closed — isn't tagged as an initial note. See NETEN-2402.
              const isInitialEvaluation =
                isInitialEvaluationSessionType(
                  appointment.suggestedSessionType,
                  resolveDAEngageProgramName(appointmentEpisode.program_name, isEngageDAProvider(userRoles)),
                ) ?? appointment.sessionNumber <= 1

              navigate(CLIENT_NEW_NOTE.route, {
                state: {
                  note: {
                    title: `Session ${appointment.sessionNumber + 1} Note`,
                    appointment_id: appointment.appointmentId,
                    content_meta_data: flexibleNoteMetadata,
                    note_type: isInitialEvaluation ? NoteTypes.INITIAL : NoteTypes.SESSION,
                    sessionDate: appointment.startDate,
                    sessionNumber: appointment.sessionNumber,
                    appointmentTimeslot,
                    body,
                  },
                  originRoute,
                },
              })
              return
            }
          }
        }
      }

      let draftNote = legacyFullNote?.status === NoteStatusStates.DRAFT ? legacyFullNote : undefined
      const noteAlreadyCompleted =
        legacyFullNote?.status === NoteStatusStates.COMPLETED && !appointment.isChargeReversed
      let previousCharge

      if (appointment.isChargeReversed) {
        Sentry.addBreadcrumb({
          category: 'Payments',
          message: 'Fetching reversed charge for session resubmission.',
          data: { appointmentId: appointment.appointmentId, providerId: userId, clientId },
        })

        const result = await fetchTransfers({
          provider_id: userId,
          patient_id: clientId,
          appointment_id: appointment.appointmentId,
          status: TransferStatus.REVERSED,
          showAlert: false,
        })
        if ('data' in result) {
          previousCharge = result.data
        }

        draftNote = allNotes?.find((n) => n.appointment_id === appointment.appointmentId)
        if (draftNote) {
          Sentry.addBreadcrumb({
            category: 'Payments',
            message: 'Fetching draft note for session resubmission.',
            data: { noteId: draftNote.id, appointmentId: appointment.appointmentId, providerId: userId },
          })
          const noteResult = await fetchNote(draftNote.id)
          if ('data' in noteResult) {
            draftNote = noteResult.data
          }
        }
      }

      navigate(CLIENT_END_OF_SESSION.route, {
        state: {
          appointment,
          originRoute,
          draftNote,
          previousCharge,
          noteAlreadyCompleted,
          showTransferError: !previousCharge && appointment.isChargeReversed,
        },
      })
    },
    [
      allNotes,
      dispatch,
      dispatchChargeGuardError,
      enableDAFlexibleNote,
      fetchNote,
      fetchTransfers,
      getClientEpisodes,
      getContentsByProgramId,
      goToExistingNote,
      intl,
      navigate,
      originRoute,
      userId,
      userRoles,
    ],
  )

  const completeNote = useCallback(
    async (appointment: Appointment, clientId: string) => {
      if (!notesFetched) {
        return
      }

      dispatch(setSelectedClientId(clientId))
      dispatch(setAppointment(appointment))

      const chargeGuard = await fetchAppointmentChargeGuard(fetchTransfers, {
        providerId: userId,
        clientId,
        appointmentId: appointment.appointmentId,
      })

      if (chargeGuard.failed) {
        dispatchChargeGuardError()
        return
      }

      const appointmentCharge = chargeGuard.pendingNoteCharge

      if (!appointmentCharge && !isEngageDAProvider(userRoles)) {
        return
      }

      // A note decides the flow on its own — the charge no longer gates this branch, so a flexible note
      // still resumes the flexible flow whatever its charge's status (ICE-7135 recovery).
      const note = allNotes?.find((n) => n.appointment_id === appointment.appointmentId)
      if (note) {
        let fullNote: Note | undefined
        if (enableDAFlexibleNote && isEngageDAProvider(userRoles)) {
          fullNote = await fetchNote(note.id).unwrap()
          if (fullNote.content_meta_data?.name === DA_FLEXIBLE_NOTE_NAME) {
            navigate(`${CLIENT_NOTE.route}?id=${note.id}`, {
              state: {
                note: { ...fullNote },
                chargeId: appointmentCharge?.id,
                originRoute,
              },
            })
            return
          }
        }
        await goToExistingNote(appointment, note, appointmentCharge, fullNote)
        return
      }

      if (!appointment.programId) {
        return
      }

      const [contents, episodes] = await Promise.all([
        getContentsByProgramId({ programId: appointment.programId }).unwrap(),
        getClientEpisodes({ providerId: userId, patientId: clientId }).unwrap(),
      ])

      const appointmentEpisode = episodes?.find((e) => e.id === appointment.episodeId)
      if (!contents || !appointmentEpisode) {
        return
      }

      // Prefer suggestedSessionType when present; otherwise fall back to sessionNumber.
      const isInitialEvaluation =
        isInitialEvaluationSessionType(
          appointment.suggestedSessionType,
          resolveDAEngageProgramName(appointmentEpisode.program_name, isEngageDAProvider(userRoles)),
        ) ?? appointment.sessionNumber <= 1

      // No note exists, so any charge on this appointment means the session was closed through the legacy
      // flow and only needs a note — serving the merged flexible note would re-submit the payment and be
      // rejected as `duplicate_payment` (ICE-7346). Falls through to the legacy note creation below.
      if (enableDAFlexibleNote && isEngageDAProvider(userRoles) && !chargeGuard.hasCharge) {
        const flexibleNoteMetadata = contents.find((c) => c.name === DA_FLEXIBLE_NOTE_NAME)
        if (flexibleNoteMetadata) {
          const body = populateInitialValues([], DA_FLEXIBLE_NOTE_NAME, contents)
          const appointmentTimeslot = getAppointmentTimeslot({ appointment, intl })

          navigate(CLIENT_NEW_NOTE.route, {
            state: {
              note: {
                title: `Session ${appointment.sessionNumber + 1} Note`,
                appointment_id: appointment.appointmentId,
                content_meta_data: flexibleNoteMetadata,
                note_type: isInitialEvaluation ? NoteTypes.INITIAL : NoteTypes.SESSION,
                sessionDate: appointment.startDate,
                sessionNumber: appointment.sessionNumber,
                appointmentTimeslot,
                body,
              },
              originRoute,
            },
          })
          return
        }
      }

      const { INITIAL_NOTE, SESSION_NOTE } =
        Programs.getProgramConfigByName(appointmentEpisode.program_name)?.noteContentMetadataName ?? {}
      const noteContentMetaDataName = isInitialEvaluation ? INITIAL_NOTE : SESSION_NOTE
      const noteContentMetaData = contents.find((c) => c.name === noteContentMetaDataName)
      if (!noteContentMetaData) {
        return
      }

      const body = populateInitialValues([], noteContentMetaDataName, contents)
      const appointmentTimeslot = getAppointmentTimeslot({ appointment, intl })

      navigate(CLIENT_NEW_NOTE.route, {
        state: {
          note: {
            title: `Session ${appointment.sessionNumber + 1} Note`,
            appointment_id: appointment.appointmentId,
            content_meta_data: noteContentMetaData,
            note_type: isInitialEvaluation ? NoteTypes.INITIAL : NoteTypes.SESSION,
            sessionDate: appointment.startDate,
            sessionNumber: appointment.sessionNumber,
            appointmentTimeslot,
            body,
          },
          chargeId: appointmentCharge?.id,
          needToUpdateCharge: appointment.changeReason !== 'clientIneligible',
          originRoute,
        },
      })
    },
    [
      allNotes,
      dispatch,
      dispatchChargeGuardError,
      enableDAFlexibleNote,
      fetchNote,
      fetchTransfers,
      getClientEpisodes,
      getContentsByProgramId,
      goToExistingNote,
      intl,
      navigate,
      notesFetched,
      originRoute,
      userId,
      userRoles,
    ],
  )

  const viewCompletedNote = useCallback(
    async (appointment: Appointment) => {
      const note = allNotes?.find((n) => n.appointment_id === appointment.appointmentId)
      if (note) {
        await goToExistingNote(appointment, note)
      }
    },
    [allNotes, goToExistingNote],
  )

  return { closeSession, completeNote, viewCompletedNote, isLoadingNotes }
}
